/**
 * Candidate communication outcomes, for the reports (Phase 5 of docs/candidate-communications-experience.md).
 *
 * The server records one event per outcome in `commEvents/{id}`, kept 90 days. Every 10 minutes a rollup adds the
 * new events to counters: per workspace per day, per job per month, and for the platform per day (split by whether
 * the application's first contact was automatic). The reports read those counters, never the events.
 *
 * An outcome that can be reported twice (a retried task, a redelivered webhook) has an id of its own making
 * (`commEventId`): the second write of an id is refused, so it counts once.
 */
import { CandidateMessageIntent, } from './clientServerApi';
const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
/** How long raw events are kept (a Firestore TTL policy on `expireAt`); the counters are kept for good. */
export const COMM_EVENT_TTL_MS = 90 * DAY_MS;
/** Days of a workspace's counters its report reads: two 30-day periods, this one and the one before. */
export const COMM_REPORT_DAYS = 60;
/** Months of each job's counters the report reads: this one (the job dashboard shows this month's). */
export const COMM_REPORT_JOB_MONTHS = 1;
/** The most days the platform view reads. */
export const COMM_PLATFORM_MAX_DAYS = 90;
/** Outcomes the platform view lists at a time. */
export const COMM_EVENTS_PAGE_SIZE = 50;
/** Every outcome type: a stored event of a type not here (a later version's) isn't counted. */
export const COMM_EVENT_TYPES = {
    application_received: true,
    first_contact_sent: true,
    first_contact_failed: true,
    first_contact_delivered: true,
    message_sent: true,
    message_delivered: true,
    message_failed: true,
    channel_fallback: true,
    candidate_replied: true,
    first_reply: true,
    expectation_started: true,
    follow_up_sent: true,
    expectation_answered: true,
    expectation_exhausted: true,
    expectation_cancelled: true,
    qualification_started: true,
    qualification_completed: true,
    interview_booked: true,
    interview_no_show: true,
    duplicate_prevented: true,
    opted_out: true,
    recruiter_paused: true,
    recruiter_edited: true,
    recruiter_took_over: true,
};
/** Whether a value is an outcome type this version knows (an own key: not `toString` and the like). */
export function isCommEventType(value) {
    return typeof value === 'string' && Object.prototype.hasOwnProperty.call(COMM_EVENT_TYPES, value);
}
/** An event id from the parts that make the outcome unique (a document id: letters, digits, `-` and `_`). */
export function commEventId(...parts) {
    return parts.map(String).join('-').replace(/[^A-Za-z0-9_-]/g, '_').slice(0, 400);
}
/** A free-text reason as a counter key: lower case letters, digits and `_`, at most 60. */
export function commReasonKey(reason) {
    const key = reason.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 60);
    return key === '' ? 'unknown' : key;
}
/** Latency buckets, shortest first: a count each, from which reports read the median. */
export const COMM_LATENCY_BUCKETS = [
    { key: 'lt1m', upToMs: MINUTE_MS, label: 'under a minute' },
    { key: 'lt5m', upToMs: 5 * MINUTE_MS, label: 'under 5 minutes' },
    { key: 'lt15m', upToMs: 15 * MINUTE_MS, label: 'under 15 minutes' },
    { key: 'lt1h', upToMs: HOUR_MS, label: 'under an hour' },
    { key: 'lt4h', upToMs: 4 * HOUR_MS, label: 'under 4 hours' },
    { key: 'lt24h', upToMs: DAY_MS, label: 'under a day' },
    { key: 'lt72h', upToMs: 3 * DAY_MS, label: 'under 3 days' },
    { key: 'gte72h', upToMs: Number.POSITIVE_INFINITY, label: '3 days or more' },
];
export function commLatencyBucket(ms) {
    return COMM_LATENCY_BUCKETS.find((bucket) => ms < bucket.upToMs) ?? COMM_LATENCY_BUCKETS[COMM_LATENCY_BUCKETS.length - 1];
}
/** A first-follow-up delay as a counter key: `h24`, or `unknown`. */
export function commDelayKey(firstDelayHours) {
    return typeof firstDelayHours === 'number' && Number.isFinite(firstDelayHours) ? `h${firstDelayHours}` : 'unknown';
}
/** What one event adds to the counters, as paths and amounts. */
export function commCounterPaths(event) {
    const one = (...path) => ({ path, amount: 1 });
    switch (event.type) {
        case 'application_received':
            return [one('applications'), one('applicationsFrom', event.source)];
        case 'first_contact_sent':
            return [
                one('firstContact', 'sent'),
                one('firstContact', 'timeToSend', commLatencyBucket(event.sinceApplicationMs).key),
                ...(event.combined ? [one('firstContact', 'combined')] : []),
            ];
        case 'first_contact_failed':
            return [one('firstContact', 'failed'), one('firstContact', 'failures', commReasonKey(event.reason))];
        case 'first_contact_delivered':
            return [
                one('firstContact', 'delivered'),
                one('firstContact', 'timeToDeliver', commLatencyBucket(event.sinceApplicationMs).key),
            ];
        case 'message_sent':
            return [one('messages', 'sent', event.channel, event.intent)];
        case 'message_delivered':
            return [one('messages', 'delivered', event.channel)];
        case 'message_failed':
            return [one('messages', 'failed', event.channel ?? 'none'), one('failures', commReasonKey(event.reason))];
        case 'channel_fallback':
            return [one('messages', 'fallback', event.from, event.to)];
        case 'candidate_replied':
            return [one('replies', event.channel ?? 'app')];
        case 'first_reply':
            return [
                one('firstReplies', 'total'),
                ...(event.sinceFirstContactMs !== null && event.sinceFirstContactMs < DAY_MS
                    ? [one('firstReplies', 'within24h')]
                    : []),
                ...(event.sinceApplicationMs !== null
                    ? [one('firstReplies', 'sinceApplication', commLatencyBucket(event.sinceApplicationMs).key)]
                    : []),
            ];
        case 'expectation_started':
            return [
                one('expectations', 'started'),
                one('expectations', 'byAction', event.action, commDelayKey(event.firstDelayHours), 'started'),
            ];
        case 'follow_up_sent':
            return [
                one('followUps', 'sent', String(event.attempt)),
                one('followUps', 'byAction', event.action, commDelayKey(event.firstDelayHours), String(event.attempt)),
            ];
        case 'expectation_answered':
            return [
                one('expectations', 'answered', String(event.afterFollowUp)),
                one('expectations', 'responseTime', commLatencyBucket(event.sinceRequestMs).key),
                one('expectations', 'byAction', event.action, commDelayKey(event.firstDelayHours), 'answered', String(event.afterFollowUp)),
            ];
        case 'expectation_exhausted':
            return [
                one('expectations', 'exhausted'),
                one('expectations', 'byAction', event.action, commDelayKey(event.firstDelayHours), 'exhausted'),
            ];
        case 'expectation_cancelled':
            return [one('expectations', 'cancelled')];
        case 'qualification_started':
            return [one('qualification', 'started')];
        case 'qualification_completed':
            return [one('qualification', 'completed')];
        case 'interview_booked':
            return [one('interviews', 'booked')];
        case 'interview_no_show':
            return [one('interviews', 'noShow')];
        case 'duplicate_prevented':
            return [one('duplicatesPrevented', commReasonKey(event.reason))];
        case 'opted_out':
            return [one('optOuts', event.channel, event.stage)];
        case 'recruiter_paused':
            return [one('recruiter', 'paused')];
        case 'recruiter_edited':
            return [one('recruiter', 'edited')];
        case 'recruiter_took_over':
            return [one('recruiter', 'tookOver')];
    }
}
/** Add `amount` at `path` (in place). */
export function addCounter(counters, path, amount) {
    let node = counters;
    for (const key of path.slice(0, -1)) {
        const next = node[key];
        if (typeof next === 'object') {
            node = next;
        }
        else {
            const created = {};
            node[key] = created;
            node = created;
        }
    }
    const leaf = path[path.length - 1];
    const current = node[leaf];
    node[leaf] = (typeof current === 'number' ? current : 0) + amount;
}
/** `into` plus `delta`, leaf by leaf (in place); returns `into`. */
export function addCounters(into, delta) {
    const walk = (node, prefix) => {
        for (const [key, value] of Object.entries(node)) {
            if (typeof value === 'number')
                addCounter(into, [...prefix, key], value);
            else
                walk(value, [...prefix, key]);
        }
    };
    walk(delta, []);
    return into;
}
/** The count at `path`; 0 where there's none. */
export function countAt(counters, ...path) {
    let node = counters;
    for (const key of path) {
        if (typeof node !== 'object')
            return 0;
        node = node[key];
    }
    return typeof node === 'number' ? node : 0;
}
/** Every count under `path` added up. */
export function totalAt(counters, ...path) {
    let node = counters;
    for (const key of path) {
        if (typeof node !== 'object')
            return 0;
        node = node[key];
    }
    if (typeof node === 'number')
        return node;
    if (node === undefined)
        return 0;
    return Object.values(node).reduce((sum, value) => sum + (typeof value === 'number' ? value : totalAt(value)), 0);
}
/** The keys directly under `path` with their totals. */
export function entriesAt(counters, ...path) {
    let node = counters;
    for (const key of path) {
        if (typeof node !== 'object')
            return [];
        node = node[key];
    }
    if (typeof node !== 'object')
        return [];
    return Object.keys(node).map((key) => ({ key, count: totalAt(counters, ...path, key) }));
}
// ── Rollup documents ───────────────────────────────────────────────────────────────────────────────────────────
/** A UTC day, `YYYY-MM-DD`: the rollups count by the day an outcome happened. */
export function commDay(atUtc) {
    return new Date(atUtc).toISOString().slice(0, 10);
}
/** A UTC month, `YYYY-MM`. */
export function commMonth(atUtc) {
    return new Date(atUtc).toISOString().slice(0, 7);
}
/** The `count` UTC days up to the one `nowUtc` falls in, oldest first. */
export function commRecentDays(nowUtc, count) {
    return Array.from({ length: count }, (_, index) => commDay(nowUtc - (count - 1 - index) * DAY_MS));
}
/** The `count` UTC months up to the one `nowUtc` falls in, oldest first. */
export function commRecentMonths(nowUtc, count) {
    const now = new Date(nowUtc);
    return Array.from({ length: count }, (_, index) => new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - (count - 1 - index), 1)).toISOString().slice(0, 7));
}
/** Counters added up over the given days (those absent count nothing). */
export function commCountersOver(days, from, to) {
    const total = {};
    for (const { day, counters } of days) {
        if (day >= from && day <= to)
            addCounters(total, counters);
    }
    return total;
}
export function commCohortMetrics(counters) {
    const applications = countAt(counters, 'applications');
    const withinDay = COMM_LATENCY_BUCKETS
        .filter((bucket) => bucket.upToMs <= DAY_MS)
        .reduce((sum, bucket) => sum + countAt(counters, 'firstReplies', 'sinceApplication', bucket.key), 0);
    const started = countAt(counters, 'qualification', 'started');
    const booked = countAt(counters, 'interviews', 'booked');
    return {
        applications,
        replied: rate(countAt(counters, 'firstReplies', 'total'), applications),
        repliedWithinDay: rate(withinDay, applications),
        medianTimeToReply: commMedianBucket(counters, 'firstReplies', 'sinceApplication'),
        screeningStarted: rate(started, applications),
        screeningCompleted: rate(countAt(counters, 'qualification', 'completed'), started),
        interviewBooked: rate(booked, applications),
        noShow: rate(countAt(counters, 'interviews', 'noShow'), booked),
    };
}
export function commFunnel(counters) {
    return {
        applied: countAt(counters, 'applications'),
        reached: countAt(counters, 'firstContact', 'delivered'),
        replied: countAt(counters, 'firstReplies', 'total'),
        screened: countAt(counters, 'qualification', 'completed'),
        booked: countAt(counters, 'interviews', 'booked'),
    };
}
/** A job's funnel for a month (`YYYY-MM`), or null when the job has no outcomes then. */
export function commJobMonthFunnel(jobMonths, jobId, month) {
    const entry = jobMonths.find((candidate) => candidate.jobId === jobId && candidate.month === month);
    return entry ? commFunnel(entry.counters) : null;
}
/** An update entry's identity: the counters it adds to and the rollup part (watermark) it came from. */
function commUpdateKey(key, throughUtc) {
    return `${key}@${throughUtc}`;
}
/**
 * Loaded counters plus a live update (`COMM_METRICS_UPDATED`). An update entry counts only if the counters were read
 * before its rollup part (their `throughUtc` is earlier than its) and it isn't in `counted` (the entries added so far):
 * an update that crossed the load, or came twice, counts once, in whatever order updates come. Loaded entries keep
 * the `throughUtc` they were read with; an entry an update creates has 0 (nothing read). Returns the new counters (a
 * copy: `current` is untouched; days oldest first) and the update entries added, or null when it adds nothing.
 */
export function addCommMetricsUpdate(current, update, counted) {
    const added = [];
    const merge = (entries, deltas, keyOf) => {
        const byKey = new Map(entries.map((entry) => [keyOf(entry), entry]));
        for (const delta of deltas) {
            const key = keyOf(delta);
            const updateKey = commUpdateKey(key, delta.throughUtc);
            const existing = byKey.get(key);
            const readThrough = existing?.throughUtc ?? 0;
            if (delta.throughUtc <= readThrough || counted.has(updateKey) || added.includes(updateKey))
                continue;
            const counters = addCounters(structuredClone(existing?.counters ?? {}), delta.counters);
            byKey.set(key, { ...delta, counters, throughUtc: readThrough });
            added.push(updateKey);
        }
        return [...byKey.values()];
    };
    const days = merge(current.days, update.days, (entry) => `day:${entry.day}`)
        .sort((a, b) => a.day.localeCompare(b.day));
    const jobMonths = merge(current.jobMonths, update.jobMonths, (entry) => `job:${entry.jobId}_${entry.month}`);
    return added.length > 0 ? { snapshot: { days, jobMonths }, added } : null;
}
function rate(count, of) {
    return { count, of, value: of > 0 ? count / of : null };
}
/** The bucket the median falls in; null with nothing counted. */
export function commMedianBucket(counters, ...path) {
    const counts = COMM_LATENCY_BUCKETS.map((bucket) => countAt(counters, ...path, bucket.key));
    const total = counts.reduce((sum, count) => sum + count, 0);
    if (total === 0)
        return null;
    let seen = 0;
    for (let index = 0; index < counts.length; index++) {
        seen += counts[index];
        if (seen * 2 >= total)
            return COMM_LATENCY_BUCKETS[index];
    }
    return COMM_LATENCY_BUCKETS[COMM_LATENCY_BUCKETS.length - 1];
}
const byCount = (a, b) => b.count - a.count;
/** The report's numbers, from counters (a day's, or several days' added up). */
export function commMetrics(counters) {
    const applications = countAt(counters, 'applications');
    const firstContactSent = countAt(counters, 'firstContact', 'sent');
    const messagesSent = totalAt(counters, 'messages', 'sent');
    const started = countAt(counters, 'expectations', 'started');
    const answered = (followUps) => countAt(counters, 'expectations', 'answered', String(followUps));
    const attempts = entriesAt(counters, 'followUps', 'sent')
        .map(({ key, count }) => ({ attempt: Number(key), sent: count }))
        .filter(({ attempt }) => Number.isInteger(attempt) && attempt > 0)
        .sort((a, b) => a.attempt - b.attempt);
    const qualificationStarted = countAt(counters, 'qualification', 'started');
    const qualificationCompleted = countAt(counters, 'qualification', 'completed');
    const booked = countAt(counters, 'interviews', 'booked');
    const optOuts = entriesAt(counters, 'optOuts').flatMap(({ key: channel }) => entriesAt(counters, 'optOuts', channel).map(({ key: stage, count }) => ({ channel, stage, count })));
    const automatedSent = entriesAt(counters, 'messages', 'sent').reduce((sum, { key: channel, count }) => sum + count - countAt(counters, 'messages', 'sent', channel, CandidateMessageIntent.RecruiterMessage), 0);
    return {
        applications,
        firstContact: {
            sent: firstContactSent,
            failed: countAt(counters, 'firstContact', 'failed'),
            combined: countAt(counters, 'firstContact', 'combined'),
            deliveryRate: rate(countAt(counters, 'firstContact', 'delivered'), firstContactSent),
            medianTimeToSend: commMedianBucket(counters, 'firstContact', 'timeToSend'),
            medianTimeToDeliver: commMedianBucket(counters, 'firstContact', 'timeToDeliver'),
        },
        replies: {
            total: totalAt(counters, 'replies'),
            within24hRate: rate(countAt(counters, 'firstReplies', 'within24h'), firstContactSent),
            medianTimeFromApplication: commMedianBucket(counters, 'firstReplies', 'sinceApplication'),
        },
        followUps: {
            sent: totalAt(counters, 'followUps', 'sent'),
            answeredWithoutFollowUp: rate(answered(0), started),
            byAttempt: attempts.map(({ attempt, sent }) => ({ attempt, sent, answeredAfter: rate(answered(attempt), sent) })),
            // Of requests made: one answered after it ran out counts as answered too.
            exhaustedRate: rate(countAt(counters, 'expectations', 'exhausted'), started),
            medianResponseTime: commMedianBucket(counters, 'expectations', 'responseTime'),
        },
        qualification: {
            started: qualificationStarted,
            completed: qualificationCompleted,
            startRate: rate(qualificationStarted, applications),
            completionRate: rate(qualificationCompleted, qualificationStarted),
        },
        interviews: {
            booked,
            noShow: countAt(counters, 'interviews', 'noShow'),
            bookingRate: rate(booked, applications),
            noShowRate: rate(countAt(counters, 'interviews', 'noShow'), booked),
        },
        channels: {
            sent: entriesAt(counters, 'messages', 'sent').map(({ key, count }) => ({ channel: key, count })).sort(byCount),
            fallbackRate: rate(totalAt(counters, 'messages', 'fallback'), messagesSent),
            deliveryRate: rate(totalAt(counters, 'messages', 'delivered'), messagesSent),
        },
        duplicatesPrevented: {
            total: totalAt(counters, 'duplicatesPrevented'),
            byReason: entriesAt(counters, 'duplicatesPrevented').sort(byCount),
        },
        optOuts: { rate: rate(totalAt(counters, 'optOuts'), messagesSent), byChannelAndStage: optOuts.sort(byCount) },
        recruiter: {
            pauseRate: rate(countAt(counters, 'recruiter', 'paused'), applications),
            editRate: rate(countAt(counters, 'recruiter', 'edited'), automatedSent),
            takeoverRate: rate(countAt(counters, 'recruiter', 'tookOver'), started),
        },
        failures: entriesAt(counters, 'failures').sort(byCount).slice(0, 5),
    };
}
